// License Assignment Service (attach a spare license to an existing app)
// -----------------------------------------------------------------------
// Client Owner-only. Licenses normally bind to a web app when that app is
// created, which leaves no way back for an app whose license LAPSED — its
// subscription was cancelled, so its slot went inactive. Without this call the
// only remedy is deleting the app and re-creating it, losing its pentest
// history.
//
// The BE (handlers/billingmanagedatasource.assign_license_to_web_application)
// owns every rule: ClientOwner-only, tenant from the JWT, the app re-resolved
// against the caller's own tenant, and a refusal if the app already holds a
// live license. This module only carries the request and maps the error code
// to copy.
//
// The mutation string is defined here rather than in the generated
// mutations.ts, which codegen overwrites.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getGraphData } from "@/utils/transform";

export interface AssignLicenseResult {
  entitlementId: string | null;
  webApplicationId: string | null;
  // null on success; otherwise a BE code (FORBIDDEN | ACCOUNT_NOT_ACTIVE |
  // WEBAPP_NOT_FOUND | ALREADY_LICENSED | NO_FREE_LICENSE |
  // LICENSE_UNAVAILABLE | CHECK_ERROR | ASSIGN_FAILED).
  error: string | null;
}

const ASSIGN_LICENSE_TO_WEB_APPLICATION = `mutation AssignLicenseToWebApplication($input: AssignLicenseToWebApplicationInput!) {
  assignLicenseToWebApplication(input: $input) {
    entitlementId
    webApplicationId
    error
  }
}`;

// Same wrapper the other authenticated services use: forces Cognito user-pools
// auth (id token) and asserts the client never sends a tenant id.
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

interface GraphQLResponse {
  data?: Record<string, unknown>;
  errors?: Array<{ message?: string }>;
}

/** Attach a license to an existing web app. Omit entitlementId to let the
 *  backend pick any available license. Never throws — a transport failure comes
 *  back as an error code, so callers have one path to handle. */
export async function assignLicenseToWebApplication(
  webApplicationId: string,
  entitlementId?: string | null,
): Promise<AssignLicenseResult> {
  try {
    const response = (await API.graphql(
      graphqlOperation(ASSIGN_LICENSE_TO_WEB_APPLICATION, {
        input: { webApplicationId, entitlementId: entitlementId ?? null },
      })
    )) as GraphQLResponse;
    const result = getGraphData(response, "assignLicenseToWebApplication") as
      | AssignLicenseResult
      | null;
    if (!result) {
      return { entitlementId: null, webApplicationId: null, error: "ASSIGN_FAILED" };
    }
    return result;
  } catch (assignError: any) {
    // A rejected resolver (e.g. $util.unauthorized) surfaces here, not as a
    // result payload — so it has to map to the same shape.
    const message =
      assignError?.errors?.[0]?.message ||
      assignError?.message ||
      "ASSIGN_FAILED";
    return { entitlementId: null, webApplicationId: null, error: message };
  }
}

// User-facing copy per BE error code. ALREADY_LICENSED and NO_FREE_LICENSE are
// the two a user can act on, so they say what to do rather than what failed.
export function assignLicenseErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case "FORBIDDEN":
      return "Only an account owner can attach licenses.";
    case "ACCOUNT_NOT_ACTIVE":
      return "Your account isn't active. Contact support if you believe this is an error.";
    case "WEBAPP_NOT_FOUND":
      return "That app couldn't be found. Refresh the page and try again.";
    case "ALREADY_LICENSED":
      return "This app already has a license.";
    case "NO_FREE_LICENSE":
      return "You have no spare licenses. Buy one above, then attach it here.";
    case "LICENSE_UNAVAILABLE":
      return "That license is no longer available. Choose another.";
    case "CHECK_ERROR":
      return "We couldn't verify your licenses just now. Please try again shortly.";
    default:
      return "We couldn't attach the license. Please try again.";
  }
}
